/**
 * Fredhopper subscriber for the generic tracking emitter.
 *
 * Maps vendor-agnostic business events to Fredhopper activity events.
 * Add new event mappings here as tracking requirements are defined.
 *
 * IMPORTANT: This module subscribes eagerly at import time (bottom of file),
 * for the same reason as the Bloomreach subscriber: SSR-hydrated components
 * (lister, PDP) emit events before the Bootstrap component mounts and calls
 * initFredhopper(). Events emitted before init are queued in fredhopperClient
 * and flushed once the SDK is initialized.
 */
import { emitter, EmitterEvent } from '../emitter';
import { FredhopperActivityAction } from './constants';
import { trackFredhopper } from './fredhopperClient';
const EVENT_MAP = {
    [EmitterEvent.VIEW_ITEM]: data => {
        trackFredhopper(FredhopperActivityAction.VIEW, { product: String(data.product_id) });
    },
    [EmitterEvent.CATEGORY_VIEW]: data => {
        // pathToPage must be the actual page path, not a display label — data.category_name
        // (from buildCategoryViewPayload) is a human-readable joined string built for
        // Bloomreach's category dimension and isn't a valid Fredhopper target.
        trackFredhopper(FredhopperActivityAction.VIEW, { pathToPage: window.location.pathname }, undefined, data.source_id);
    },
    [EmitterEvent.CART_UPDATE]: data => {
        const action = data.action === 'remove' ? FredhopperActivityAction.REMOVE_FROM_CART : FredhopperActivityAction.ADD_TO_CART;
        const metadata = action === FredhopperActivityAction.ADD_TO_CART ? { quantity: data.total_quantity } : undefined;
        trackFredhopper(action, { product: String(data.product_id) }, metadata);
    },
    [EmitterEvent.LOGOUT]: () => {
        // No dedicated identity to clear yet — Fredhopper sessions key off the
        // shared visitor id (see fredhopperClient.getSessionId), not login state.
    },
};
let subscribed = false;
/** Subscribes to the generic tracking emitter. */
export const subscribeFredhopperToTracking = () => {
    if (subscribed) {
        return;
    }
    subscribed = true;
    Object.entries(EVENT_MAP).forEach(([event, handler]) => {
        emitter.on(event, handler);
    });
};
// Subscribe eagerly so SSR-hydrated components (lister, PDP) can emit events
// before Bootstrap mounts. Events are queued in fredhopperClient until init.
subscribeFredhopperToTracking();
/** Unsubscribes from the generic tracking emitter. */
export const unsubscribeFredhopperFromTracking = () => {
    Object.entries(EVENT_MAP).forEach(([event, handler]) => {
        emitter.off(event, handler);
    });
    subscribed = false;
};
